import { html, render } from 'lit';
import {
  landingEditorCommentAnchorId,
  landingEditorCommentElementId,
  type LandingEditorComment,
  type LandingEditorCommentAnchor,
} from '../../shared/editor/landingEditorComments.js';
import { editorNodeIdClass } from '../editor/landingEditorRender.js';
import { editorPreviewFrames } from './editorPreviewViewport.js';

export interface EditorCommentScreenPoint {
  left: number;
  top: number;
}

interface CommentOverlayOptions {
  comments: LandingEditorComment[];
  activeCommentId: string | null;
  pageKey: LandingEditorCommentAnchor['pageKey'];
  audience: LandingEditorCommentAnchor['audience'];
  elementIds: readonly string[];
  anchorIds: Readonly<Record<string, string>>;
  onActivateProfile: (profileId: string) => void;
  onCreate: (anchor: LandingEditorCommentAnchor, point: EditorCommentScreenPoint) => void;
  onMove: (comment: LandingEditorComment, anchor: LandingEditorCommentAnchor) => void;
  onSelect: (comment: LandingEditorComment, point: EditorCommentScreenPoint) => void;
}

type OverlayController = { page: HTMLElement; click: (event: MouseEvent) => void; observer: ResizeObserver | null };
const controllers = new WeakMap<HTMLIFrameElement, OverlayController>();

const screenPoint = (frame: HTMLIFrameElement, x: number, y: number): EditorCommentScreenPoint => {
  const rect = frame.getBoundingClientRect();
  const scale = frame.offsetWidth ? rect.width / frame.offsetWidth : 1;
  return { left: rect.left + frame.clientLeft * scale + x * scale, top: rect.top + frame.clientTop * scale + y * scale };
};

const elementIdAt = (origin: Element | null, ids: readonly string[]) => {
  const node = origin?.closest('.vp-editor-node');
  if (!node) return 'page.root';
  return ids.find(id => node.classList.contains(editorNodeIdClass(id))) ?? 'page.root';
};

const anchorAt = (
  page: HTMLElement,
  clientX: number,
  clientY: number,
  pageKey: LandingEditorCommentAnchor['pageKey'],
  audience: LandingEditorCommentAnchor['audience'],
  elementIds: readonly string[],
  anchorIds: Readonly<Record<string, string>>,
): LandingEditorCommentAnchor => {
  const documentAtPoint = page.ownerDocument;
  const hit = documentAtPoint.elementFromPoint(clientX, clientY);
  const elementId = elementIdAt(hit, elementIds);
  const anchorElement =
    elementId === 'page.root' ? page : page.querySelector<HTMLElement>(`.${editorNodeIdClass(elementId)}`) ?? page;
  const rect = anchorElement.getBoundingClientRect();
  return {
    pageKey,
    audience,
    elementId: anchorElement === page ? 'page.root' : landingEditorCommentAnchorId(anchorIds, elementId),
    x: Math.min(1, Math.max(0, rect.width ? (clientX - rect.left) / rect.width : 0.5)),
    y: Math.min(1, Math.max(0, rect.height ? (clientY - rect.top) / rect.height : 0.5)),
  };
};

const pinPosition = (page: HTMLElement, comment: LandingEditorComment, anchorIds: Readonly<Record<string, string>>) => {
  const elementId = landingEditorCommentElementId(anchorIds, comment.anchor.elementId);
  const target =
    elementId === 'page.root'
      ? page
      : page.querySelector<HTMLElement>(`.${editorNodeIdClass(elementId)}`);
  if (!target) return null;
  if (!target.getClientRects().length || target.ownerDocument.defaultView?.getComputedStyle(target).display === 'none')
    return null;
  const pageRect = page.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return {
    left: targetRect.left - pageRect.left + targetRect.width * comment.anchor.x,
    top: targetRect.top - pageRect.top + targetRect.height * comment.anchor.y,
  };
};

const startPinDrag = (
  event: PointerEvent,
  pin: HTMLButtonElement,
  page: HTMLElement,
  comment: LandingEditorComment,
  options: CommentOverlayOptions,
) => {
  if (event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();
  const startX = event.clientX;
  const startY = event.clientY;
  let moved = false;
  let frameRequest = 0;
  let nextX = startX;
  let nextY = startY;
  pin.setPointerCapture(event.pointerId);

  const paint = () => {
    frameRequest = 0;
    pin.style.transform = `translate(-5px, -25px) translate3d(${nextX - startX}px, ${nextY - startY}px, 0)`;
  };
  const move = (moveEvent: PointerEvent) => {
    nextX = moveEvent.clientX;
    nextY = moveEvent.clientY;
    moved ||= Math.hypot(nextX - startX, nextY - startY) > 3;
    if (!frameRequest) frameRequest = requestAnimationFrame(paint);
  };
  const finish = (finishEvent: PointerEvent) => {
    pin.removeEventListener('pointermove', move);
    pin.removeEventListener('pointerup', finish);
    pin.removeEventListener('pointercancel', cancel);
    if (frameRequest) cancelAnimationFrame(frameRequest);
    pin.style.transform = '';
    if (!moved || finishEvent.type === 'pointercancel') {
      if (!moved) {
        const frame = page.ownerDocument.defaultView?.frameElement;
        if (frame instanceof HTMLIFrameElement)
          options.onSelect(comment, screenPoint(frame, finishEvent.clientX, finishEvent.clientY));
      }
      return;
    }
    pin.style.pointerEvents = 'none';
    const anchor = anchorAt(
      page,
      finishEvent.clientX,
      finishEvent.clientY,
      comment.anchor.pageKey,
      comment.anchor.audience,
      options.elementIds,
      options.anchorIds,
    );
    pin.style.pointerEvents = '';
    options.onMove(comment, anchor);
  };
  const cancel = (cancelEvent: PointerEvent) => finish(cancelEvent);
  pin.addEventListener('pointermove', move);
  pin.addEventListener('pointerup', finish);
  pin.addEventListener('pointercancel', cancel);
};

const renderPins = (frame: HTMLIFrameElement, page: HTMLElement, layer: HTMLElement, options: CommentOverlayOptions) => {
  const visible = options.comments.filter(comment => comment.status === 'open').flatMap((comment, index) => {
    const position = pinPosition(page, comment, options.anchorIds);
    if (!position) return [];
    return [html`
      <button
        type="button"
        data-comment-id=${comment.id}
        class="vp-editor-comment-pin ${options.activeCommentId === comment.id ? 'is-active' : ''}"
        style=${`left:${position.left}px;top:${position.top}px;`}
        title="Kommentar öffnen oder verschieben"
        aria-label=${`Kommentar ${index + 1}: ${comment.text}`}
        @pointerdown=${(event: PointerEvent) =>
          startPinDrag(event, event.currentTarget as HTMLButtonElement, page, comment, options)}
        @keydown=${(event: KeyboardEvent) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const step = event.shiftKey ? 0.1 : 0.02;
          const anchor = { ...comment.anchor };
          if (event.key === 'ArrowLeft') anchor.x = Math.max(0, anchor.x - step);
          if (event.key === 'ArrowRight') anchor.x = Math.min(1, anchor.x + step);
          if (event.key === 'ArrowUp') anchor.y = Math.max(0, anchor.y - step);
          if (event.key === 'ArrowDown') anchor.y = Math.min(1, anchor.y + step);
          options.onMove(comment, anchor);
        }}
      ><span class="icon-[material-symbols--chat-bubble-outline]"></span><small>${index + 1}</small></button>
    `];
  });
  render(visible, layer);
};

export const syncEditorCommentOverlays = (options: CommentOverlayOptions) => {
  for (const frame of editorPreviewFrames()) {
    const page = frame.contentDocument?.querySelector<HTMLElement>('.vp-editor-preview-page');
    if (!page) continue;
    let layer = page.querySelector<HTMLElement>(':scope > .vp-editor-comment-layer');
    if (!layer) {
      layer = page.ownerDocument.createElement('div');
      layer.className = 'vp-editor-comment-layer';
      page.append(layer);
    }
    const previous = controllers.get(frame);
    if (previous) {
      previous.page.removeEventListener('click', previous.click, true);
      previous.observer?.disconnect();
    }
    const click = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('.vp-editor-comment-pin')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      options.onActivateProfile(frame.dataset.profileId ?? 'desktop');
      options.onCreate(
        anchorAt(page, event.clientX, event.clientY, options.pageKey, options.audience, options.elementIds, options.anchorIds),
        screenPoint(frame, event.clientX, event.clientY),
      );
    };
    page.addEventListener('click', click, true);
    const currentLayer = layer;
    const paint = () => renderPins(frame, page, currentLayer, options);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(paint);
    observer?.observe(page);
    controllers.set(frame, { page, click, observer });
    paint();
  }
};

export const clearEditorCommentOverlays = () => {
  for (const frame of editorPreviewFrames()) {
    const controller = controllers.get(frame);
    if (controller) {
      controller.page.removeEventListener('click', controller.click, true);
      controller.observer?.disconnect();
      controllers.delete(frame);
    }
    frame.contentDocument?.querySelector('.vp-editor-comment-layer')?.remove();
  }
};

export const editorCommentAnchorExists = (comment: LandingEditorComment, anchorIds: Readonly<Record<string, string>>) =>
  comment.anchor.elementId === 'page.root' ||
  editorPreviewFrames().some(frame =>
    Boolean(frame.contentDocument?.querySelector(`.${editorNodeIdClass(landingEditorCommentElementId(anchorIds, comment.anchor.elementId))}`)),
  );

export const scrollEditorCommentIntoView = (commentId: string) => {
  const active = document.querySelector<HTMLIFrameElement>('iframe.vp-editor-frame.is-active');
  const pin = active?.contentDocument?.querySelector<HTMLElement>(`[data-comment-id="${CSS.escape(commentId)}"]`);
  pin?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
};
